/**
 * Attachment URL parsing and classification logic for PDF generation.
 * Extracted from FarmDetailsModal.tsx.
 */

/**
 * Recursively find URLs in form answers.
 * Handles URLs in various formats:
 * - Direct URLs: "https://example.com"
 * - Invalid JSON arrays: "[https://url1.com, https://url2.com]"
 * - Valid JSON arrays: ["https://url1.com", "https://url2.com"]
 */
export function findUrls(value: any): string[] {
  if (typeof value === "string") {
    // Try to parse as JSON first (valid arrays)
    if (value.startsWith("[") || value.startsWith("{")) {
      try {
        const parsed = JSON.parse(value);
        // Recursively process the parsed value
        return findUrls(parsed);
      } catch {
        // Not valid JSON, might be invalid array format like "[url1, url2]"
        if (value.startsWith("[") && value.endsWith("]")) {
          // Remove brackets and split by comma
          const inner = value.slice(1, -1).trim();
          if (!inner) return [];
          // Split by comma and clean up each URL
          const items = inner.split(",").map(s => s.trim()).filter(s => s !== "");
          // Recursively check each item
          return items.flatMap(item => findUrls(item));
        }
      }
    }
    // Check if it's a direct URL
    if (value.startsWith("http://") || value.startsWith("https://")) {
      return [value];
    }
  }
  if (Array.isArray(value)) {
    return value.flatMap(findUrls);
  }
  if (typeof value === "object" && value !== null) {
    return Object.values(value).flatMap(findUrls);
  }
  return [];
}

/**
 * Parse answer JSON and extract URLs.
 */
export function extractUrlsFromAnswersJson(jsonString?: string): string[] {
  if (!jsonString) return [];
  try {
    const parsedData = JSON.parse(jsonString);
    if (Array.isArray(parsedData) && parsedData[0]?.questionId) {
      return parsedData.flatMap((item) => findUrls(item.value));
    }
    return findUrls(parsedData);
  } catch (e) {
    if (
      typeof jsonString === "string" &&
      (jsonString.startsWith("http://") || jsonString.startsWith("https://"))
    ) {
      return [jsonString];
    }
  }
  return [];
}

/**
 * Attachment content type and buffer result.
 */
export interface AttachmentContentResult {
  url: string;
  contentType: string | null;
  pdfBuffer: ArrayBuffer | null;
  videoThumbnail: string | null;
  imageDataUrl: string | null;
}

/**
 * Fetch content type and process attachments.
 * Returns PDF buffers, video thumbnails, and image data URLs.
 */
export async function processAttachments(
  urls: string[],
  generateVideoThumbnail: (url: string) => Promise<string | null>,
  convertImageToDataUrl: (url: string) => Promise<string | null>,
): Promise<AttachmentContentResult[]> {
  const promises = urls.map(async (url) => {
    const cacheBustedUrl = `${url}?_t=${new Date().getTime()}`;
    try {
      const headResponse = await fetch(cacheBustedUrl, {
        method: "HEAD",
      }).catch(() => null);
      if (!headResponse) {
        return {
          url,
          contentType: null,
          pdfBuffer: null,
          videoThumbnail: null,
          imageDataUrl: null,
        };
      }

      if (headResponse.ok) {
        const contentType = headResponse.headers.get("Content-Type");

        if (contentType?.includes("pdf")) {
          const getResponse = await fetch(cacheBustedUrl);
          const pdfBuffer = await getResponse.arrayBuffer();
          return {
            url,
            contentType,
            pdfBuffer,
            videoThumbnail: null,
            imageDataUrl: null,
          };
        }

        if (contentType?.startsWith("video/")) {
          const videoThumbnail = await generateVideoThumbnail(cacheBustedUrl);
          return {
            url,
            contentType,
            pdfBuffer: null,
            videoThumbnail,
            imageDataUrl: null,
          };
        }

        if (contentType?.startsWith("image/")) {
          const imageDataUrl = await convertImageToDataUrl(cacheBustedUrl);
          return {
            url,
            contentType,
            pdfBuffer: null,
            videoThumbnail: null,
            imageDataUrl,
          };
        }

        return {
          url,
          contentType,
          pdfBuffer: null,
          videoThumbnail: null,
          imageDataUrl: null,
        };
      }
    } catch (e) {
      console.error(`Could not fetch HEAD for ${cacheBustedUrl}:`, e);
    }
    return {
      url,
      contentType: null,
      pdfBuffer: null,
      videoThumbnail: null,
      imageDataUrl: null,
    };
  });

  return await Promise.all(promises);
}

/**
 * Result of organizing attachment content.
 */
export interface OrganizedAttachmentContent {
  contentTypes: Record<string, string | null>;
  videoThumbnails: Record<string, string | null>;
  imageDataUrls: Record<string, string | null>;
  attachedPdfs: { url: string; buffer: ArrayBuffer }[];
}

/**
 * Organize attachment content results into lookup maps and PDF arrays.
 */
export function organizeAttachmentContent(
  results: AttachmentContentResult[],
): OrganizedAttachmentContent {
  const contentTypes: Record<string, string | null> = {};
  const videoThumbnails: Record<string, string | null> = {};
  const imageDataUrls: Record<string, string | null> = {};
  const attachedPdfs: { url: string; buffer: ArrayBuffer }[] = [];

  results.forEach(
    ({ url, contentType, pdfBuffer, videoThumbnail, imageDataUrl }) => {
      contentTypes[url] = contentType;
      if (pdfBuffer) {
        attachedPdfs.push({ url, buffer: pdfBuffer });
      }
      if (videoThumbnail) {
        videoThumbnails[url] = videoThumbnail;
      }
      if (imageDataUrl) {
        imageDataUrls[url] = imageDataUrl;
      }
    },
  );

  return { contentTypes, videoThumbnails, imageDataUrls, attachedPdfs };
}

/**
 * Collect all unique URLs from approved item attachments.
 * Handles both legacy format (item.value) and new format (answersMap).
 */
export function collectAttachmentUrls(
  sortedApprovedLevels: [string, any[]][],
  answersMap?: Record<string, any[]>,
): string[] {
  const allUrls = new Set<string>();
  
  console.log('[PDF Attachments] collectAttachmentUrls called', {
    levelsCount: sortedApprovedLevels.length,
    hasAnswersMap: !!answersMap,
    answersMapSize: answersMap ? Object.keys(answersMap).length : 0
  });
  
  sortedApprovedLevels.forEach(([, items]) => {
    items.forEach((item) => {
      console.log('[PDF Attachments] Processing item', {
        itemId: item.id,
        hasValue: !!item.value,
        hasAnswersInMap: answersMap ? !!answersMap[item.id] : false,
        answersCount: answersMap && answersMap[item.id] ? answersMap[item.id].length : 0
      });
      
      // Check new format first (answersMap)
      if (answersMap && answersMap[item.id]) {
        const answers = answersMap[item.id];
        answers.forEach((answer: any) => {
          console.log('[PDF Attachments] Checking answer', {
            answerId: answer.id,
            questionId: answer.questionId,
            valueType: typeof answer.value,
            valuePreview: typeof answer.value === 'string' ? answer.value.substring(0, 100) : answer.value
          });
          const urls = findUrls(answer.value);
          console.log('[PDF Attachments] Found URLs in answer:', urls);
          urls.forEach((url) => allUrls.add(url));
        });
      }
      // Also check legacy format (item.value)
      const urls = extractUrlsFromAnswersJson(item.value);
      console.log('[PDF Attachments] Found URLs in legacy value:', urls);
      urls.forEach((url) => allUrls.add(url));
    });
  });
  
  console.log('[PDF Attachments] Total URLs collected:', Array.from(allUrls));
  return Array.from(allUrls);
}
